import { createSlice, current, WritableDraft } from "@reduxjs/toolkit";
import { PagePatch, PagePost } from "../../server/types/index.js";
import { createAppSelector } from "../store/utils.js";
import {
  DragPageActionParams,
  DragPageParams,
  Language,
  Page,
  PagePostRequest,
  PageRequest,
  PagesByAlias,
  PagesById,
  PagesByParentId,
  PageState,
  PageTranslation,
  PageTranslationPostRequest,
  PageTranslationRequest,
  ThunkDispatch,
} from "../types/index.js";
import {
  customSlugify,
  fetch,
  getTopLevelMainMenu,
  getTranslationFallbackLanguage,
  isItemUntranslated,
  keys,
  PageNotFoundError,
} from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { deleteModuleTranslationStart } from "./modules/actions.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "pages";

export const initialState: PageState = {
  byId: {},
  byAlias: {},
  byParentId: { null: [] },
  systemPages: [],
  prevState: {},
};

export const getPages = createAppAsyncThunk(
  `${sliceName}/getPages`,
  async (siteId: string, { dispatch }) => {
    try {
      const data = await fetch({ dispatch })
        .get(`sites/${siteId}/pages`)
        .json<Page[]>();
      return data;
    } catch (error) {
      dispatch(showAlert("Der Seitenbaum konnte nicht geladen werden!"));
      throw error;
    }
  },
  {
    condition: (_siteId, { getState }) =>
      getState().loadStates.pages === "unloaded",
  },
);

export const postPage = createAppAsyncThunk(
  `${sliceName}/postPage`,
  async (
    { siteId, request }: { siteId: string; request: PagePostRequest },
    { dispatch },
  ) => {
    const { translation, ...rest } = request;
    const data: PagePost = {
      ...rest,
      translations: { [translation.languageId]: translation },
    };
    try {
      const page = await fetch({ dispatch })
        .post(data, `sites/${siteId}/pages`)
        .json<Page>();

      return page;
    } catch (error) {
      dispatch(
        showAlert("Beim Erstellen der Seite ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
);

export const patchPage = createAppAsyncThunk(
  `${sliceName}/patchPage`,
  async (
    {
      siteId,
      pageId,
      pageProps,
    }: { siteId: string; pageId: string; pageProps: PageRequest },
    { dispatch },
  ) => {
    try {
      return await patchPageRequest({ dispatch, siteId, pageId, pageProps });
    } catch (error) {
      dispatch(
        showAlert("Die Seiteneinstellungen konnten nicht aktualisiert werden!"),
      );
      throw error;
    }
  },
);

export const dragPage = createAppAsyncThunk(
  `${sliceName}/dragPage`,
  async (params: DragPageActionParams, { dispatch }) => {
    const { siteId, ...rest } = params;
    const drag: DragPageParams = rest;
    const { sourcePageId } = drag;

    try {
      await fetch({ dispatch })
        .post(drag, `sites/${siteId}/pages/${sourcePageId}/drag`)
        .res();
    } catch (error) {
      showAlert("Die Seite konnte nicht verschoben werden!");
      throw error;
    }
  },
);

export const postPageTranslation = createAppAsyncThunk<
  PageTranslation,
  {
    siteId: string;
    pageId: string;
    languageId: Language;
    form: PageTranslationPostRequest;
  },
  { pendingMeta: { sourceLanguageId: Language } }
>(
  `${sliceName}/postPageTranslation`,
  async ({ siteId, pageId, languageId, form }, { dispatch }) => {
    const requestData: PagePatch = { translations: { [languageId]: form } };
    try {
      const page = await fetch({ dispatch })
        .patch(requestData, `sites/${siteId}/pages/${pageId}`)
        .json<Page>();
      const pageTranslation = page.translations[languageId];
      if (!pageTranslation) {
        throw Error(
          "The posted page translation was not returned from the server.",
        );
      }
      return pageTranslation;
    } catch (error) {
      showAlert(
        `Die ${languageId.toUpperCase()} Übersetzung konnte nicht zur Seite hinzugefügt werden!`,
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ pageId }, { getState }) => {
      const page = getState().pages.byId[pageId];
      return !!page;
    },
    getPendingMeta: ({ arg: { pageId, languageId } }, { getState }) => {
      const page = getPage(getState().pages, pageId);
      return {
        sourceLanguageId: getTranslationFallbackLanguage(page, languageId),
      };
    },
  },
);

export const deletePageTranslation = createAppAsyncThunk<
  void,
  { pageId: string; languageId: Language }
>(
  `${sliceName}/deletePageTranslation`,
  async ({ pageId, languageId }, { dispatch, getState, requestId }) => {
    const { pages } = getState();
    const state = pages.prevState[requestId];
    if (!state)
      throw Error(
        "No previous state found for the delete page translation action.",
      );
    const page = getPage(state, pageId);
    const isUntranslated = isItemUntranslated(page);

    const deleteRequest: PagePatch = { translations: { [languageId]: null } };

    const url = `sites/${page.siteId}/pages/${pageId}`;
    try {
      await (isUntranslated
        ? fetch({ dispatch }).delete(url).res()
        : fetch({ dispatch }).patch(deleteRequest, url).res());
    } catch (error) {
      dispatch(
        showAlert(
          "Beim Löschen der Seiten-Übersetzung ist ein Fehler aufgetreten!",
        ),
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ pageId }, { getState }) => !!getState().pages.byId[pageId],
  },
);

const patchPageRequest = async ({
  dispatch,
  siteId,
  pageId,
  pageProps,
}: {
  dispatch: ThunkDispatch;
  siteId: string;
  pageId: string;
  pageProps: PageRequest;
}): Promise<Page> => {
  const page = await fetch({ dispatch })
    .patch(pageProps, `sites/${siteId}/pages/${pageId}`)
    .json<Page>();
  return page;
};

const addPageToParent = (
  state: WritableDraft<PageState>,
  source: Page,
  target: Page,
  insertBefore: boolean,
  isNewSubtree: boolean,
) => {
  const newParentId = String(isNewSubtree ? target.id : target.parentId);
  const targetParentIds = [...(state.byParentId[newParentId] ?? [])];

  let newIndex = targetParentIds.indexOf(target.id);
  newIndex = insertBefore ? newIndex : newIndex + 1;

  targetParentIds.splice(newIndex, 0, source.id);

  state.byParentId[newParentId] = targetParentIds;
};

const removePageFromParent = (
  state: WritableDraft<PageState>,
  source: Page,
) => {
  const sourceParentIds =
    state.byParentId[String(source.parentId)]?.filter(
      (id) => id !== source.id,
    ) ?? [];

  state.byParentId[String(source.parentId)] = sourceParentIds;
};

const replacePage = (state: WritableDraft<PageState>, page: Page) => {
  state.byId[page.id] = page;
};

const getRecursiveSubpages = (
  byParentId: PagesByParentId,
  pageId: string,
): string[] => {
  const subpages = byParentId[pageId] ?? [];
  if (!subpages.length) return [];

  const allSubpages = subpages.reduce<string[]>(
    (accumulator, subpageId) => [
      ...accumulator,
      ...getRecursiveSubpages(byParentId, subpageId),
    ],
    subpages,
  );

  return allSubpages;
};

export const getPagesSuccessState = (
  state: PageState,
  pages: Page[],
): PageState => {
  const [byId, byAlias, byParentId, systemPages] = pages.reduce<
    [PagesById, PagesByAlias, PagesByParentId, string[]]
  >(
    ([byId, byAlias, byParentId, systemPages], page) => {
      byId[page.id] = page;

      if (page.isSystemPage) {
        systemPages = [...systemPages, page.id];
      } else {
        const parentId = String(page.parentId);
        const byCurrentParentId: string[] = byParentId[parentId] || [];
        byParentId[parentId] = [...byCurrentParentId, page.id];
      }

      if (page.alias) {
        byAlias[page.alias] = page.id;
      }

      return [byId, byAlias, byParentId, systemPages];
    },
    [{}, {}, { null: [] }, []],
  );

  return { ...state, byId, byAlias, byParentId, systemPages };
};

const patchPageStartInternal = (
  state: WritableDraft<PageState>,
  pageId: string,
  { translations, ...pageRequestRest }: PageRequest,
) => {
  const currentPage = getPage(state, pageId);
  replacePage(state, { ...currentPage, ...pageRequestRest });

  if (!translations) {
    return;
  }

  // Update the translations too
  keys(translations).forEach((languageId) => {
    const translation = translations[languageId];
    if (!translation) return;

    return patchPageTranslationStartInternal(
      state,
      pageId,
      languageId,
      translation,
      true,
    );
  });
};

const patchPageSuccessInternal = (
  state: WritableDraft<PageState>,
  page: Page,
) => {
  const { translations, ...pageWithoutTranslations } = page;
  const existingPage = getPage(state, page.id);

  state.byId[page.id] = { ...existingPage, ...pageWithoutTranslations };

  keys(translations).forEach((languageId) => {
    const translation = translations[languageId];
    if (!translation) return;
    patchPageTranslationSuccessInternal(state, page.id, translation);
  });
};

const patchPageTranslationStartInternal = (
  state: WritableDraft<PageState>,
  pageId: string,
  languageId: Language,
  pageTranslationRequest: PageTranslationRequest,
  setUpdatedAt: boolean,
) => {
  const page = state.byId[pageId];
  const translation = page?.translations[languageId];
  if (!translation) return;

  const newTranslation: PageTranslation = {
    ...translation,
    ...pageTranslationRequest,
    updatedAt: setUpdatedAt ? new Date().toJSON() : translation.updatedAt,
  };
  const slug = newTranslation.slug && customSlugify(newTranslation.slug);

  page.translations[languageId] = { ...newTranslation, slug };
};

const patchPageTranslationSuccessInternal = (
  state: WritableDraft<PageState>,
  pageId: string,
  pageTranslation: PageTranslation,
) => {
  const existingPageTranslation =
    state.byId[pageId]?.translations[pageTranslation.languageId];

  // Only update the fields that are processed by the server,
  // otherwise text fields could be overwritten
  const updatedPageTranslation: PageTranslation = !existingPageTranslation
    ? pageTranslation
    : {
        ...existingPageTranslation,
        createdAt: pageTranslation.createdAt,
        updatedAt: pageTranslation.updatedAt,
        slug: pageTranslation.slug,
      };

  patchPageTranslationStartInternal(
    state,
    pageId,
    pageTranslation.languageId,
    updatedPageTranslation,
    false,
  );
};

const dragPageInternal = (
  state: WritableDraft<PageState>,
  sourcePageId: string,
  targetPageId: string,
  insertBefore: boolean,
  isNewSubtree: boolean,
) => {
  const source = getPage(state, sourcePageId);
  const target = getPage(state, targetPageId);

  removePageFromParent(state, source);
  addPageToParent(state, source, target, insertBefore, isNewSubtree);

  // Update source item
  const newParentId = isNewSubtree ? target.id : target.parentId;
  patchPageStartInternal(state, source.id, { parentId: newParentId });
};

const deletePageTranslationStartInternal = (
  state: WritableDraft<PageState>,
  pageId: string,
  languageId: Language,
) => {
  const { byId, byParentId } = state;
  const page = getPage(state, pageId);

  // Delete all subpages in all languages if there’s
  // only translation left on the current page
  if (isItemUntranslated(page)) {
    const pageIdsToDelete = [
      pageId,
      ...getRecursiveSubpages(byParentId, pageId),
    ];

    pageIdsToDelete.forEach((currentPageId) => {
      const pageToDelete = getPage(state, currentPageId);
      removePageFromParent(state, pageToDelete);
    });

    pageIdsToDelete.forEach((currentPageId) => {
      delete byId[currentPageId];
    });
  }

  delete page.translations[languageId];
};

const getPage = (state: PageState, pageId: string) => {
  const page = state.byId[pageId];
  if (!page) throw new PageNotFoundError(pageId);
  return page;
};

const setToPrevState = (state: WritableDraft<PageState>, requestId: string) => {
  const prevState = state.prevState[requestId];
  if (!prevState) return;

  delete state.prevState[requestId];
  state.byAlias = prevState.byAlias;
  state.byId = prevState.byId;
  state.byParentId = prevState.byParentId;
  state.systemPages = prevState.systemPages;
};

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getPages.fulfilled, (state, { payload: pages }) =>
      getPagesSuccessState(state, pages),
    );

    builder.addCase(postPage.fulfilled, (state, action) => {
      const page = action.payload;
      replacePage(state, page);
      addPageToParent(state, page, page, false, false);
    });

    builder.addCase(patchPage.pending, (state, action) => {
      const { prevState, ...snapshot } = current(state);
      state.prevState[action.meta.requestId] = { ...snapshot, prevState: {} };
      patchPageStartInternal(
        state,
        action.meta.arg.pageId,
        action.meta.arg.pageProps,
      );
    });

    builder.addCase(patchPage.fulfilled, (state, action) => {
      patchPageSuccessInternal(state, action.payload);
      delete state.prevState[action.meta.requestId];
    });

    builder.addCase(patchPage.rejected, (state, action) =>
      setToPrevState(state, action.meta.requestId),
    );

    builder.addCase(dragPage.pending, (state, action) => {
      const {
        sourcePageId,
        targetPageId,
        insertBefore,
        isNewSubtree,
      }: DragPageParams = action.meta.arg;
      dragPageInternal(
        state,
        sourcePageId,
        targetPageId,
        insertBefore,
        isNewSubtree,
      );
    });

    builder.addCase(postPageTranslation.fulfilled, (state, action) => {
      const pageTranslation = action.payload;
      const page = getPage(state, action.meta.arg.pageId);

      const { languageId } = pageTranslation;
      page.translations[languageId] = pageTranslation;
    });

    builder.addCase(postPageTranslation.rejected, (state, action) => {
      if (action.meta.condition) {
        getPage(state, action.meta.arg.pageId);
      }
    });

    builder.addCase(deletePageTranslation.pending, (state, action) => {
      const { prevState, ...snapshot } = current(state);
      state.prevState[action.meta.requestId] = { ...snapshot, prevState: {} };

      const { pageId, languageId } = action.meta.arg;
      deletePageTranslationStartInternal(state, pageId, languageId);
    });

    builder.addCase(deletePageTranslation.fulfilled, (state, action) => {
      delete state.prevState[action.meta.requestId];
    });

    builder.addCase(deletePageTranslation.rejected, (state, action) => {
      if (action.meta.condition) {
        getPage(state, action.meta.arg.pageId);
      }

      setToPrevState(state, action.meta.requestId);
    });

    // Remove popUpModuleId references from pages in case a PopUpModule is deleted
    builder.addCase(
      deleteModuleTranslationStart,
      (state, { payload: { deleteAllTranslations, moduleType, moduleId } }) => {
        if (!deleteAllTranslations || moduleType !== "PopUpModule") {
          return;
        }
        Object.entries(state.byId).forEach(([_, page]) => {
          page.popUpModuleId =
            page.popUpModuleId === moduleId ? null : page.popUpModuleId;
        });
      },
    );
  },
});

export const selectTopLevelMainMenuItems = createAppSelector(
  [
    (state) => state.pages,
    (_, languageId: Language) => languageId,
    (_, _languageId: Language, isPreview: boolean) => isPreview,
    (
      _,
      _languageId: Language,
      _isPreview: boolean,
      fallbackLanguageId: Language | undefined,
    ) => fallbackLanguageId,
  ],
  (pages, languageId, isPreview, fallbackLanguageId) =>
    getTopLevelMainMenu({ pages, languageId, isPreview, fallbackLanguageId }),
);

export default slice.reducer;
